import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { firstValueFrom, fromEvent, takeUntil } from 'rxjs';

import { schemaLoader } from '@kros-sk/core/schema';

import { ApplicationCurrentSubmoduleApiService } from './application-current-submodule-api.service';
import { ApplicationSubmoduleSchema } from './application-current-submodule.types';

interface SubmoduleCriteria {
  submodule: string;
  personId: string;
  device: string | null;
}

@Injectable()
export class ApplicationCurrentSubmoduleStore {
  readonly #apiService = inject(ApplicationCurrentSubmoduleApiService);

  readonly #criteria = signal<SubmoduleCriteria | undefined>(undefined);

  readonly #loader = schemaLoader<ApplicationSubmoduleSchema, SubmoduleCriteria>({
    criteria: this.#criteria,
    /**
     * `firstValueFrom` on its own never unsubscribes, so a submodule superseded by navigation would
     * leave its request running. Tying it to the loader's abort signal restores the cancellation the
     * previous `switchMap` pipeline had, on both a superseding request and store destruction. The
     * `EmptyError` a cancelled request rejects with is harmless: `resource` discards the outcome of
     * a load whose abort signal has already fired.
     */
    load: ({ submodule, personId, device }, abortSignal) =>
      firstValueFrom(this.#apiService.get(submodule, personId, { device }).pipe(takeUntil(fromEvent(abortSignal, 'abort')))),
  });

  readonly state = this.#loader.state;

  readonly schema = this.#loader.schema;

  readonly isLoading = this.#loader.isLoading;

  /** Kept as an observable for `ApplicationSubmodulesFacade.isLoading$`, which `validateModuleGuard` awaits. */
  readonly isLoading$ = toObservable(this.isLoading);

  /** Incomplete criteria mean "do not request" — the loader stays idle. */
  setCriteria(criteria: { submodule: string | null; personId: string | null; device: string | null }): void {
    this.#criteria.set(
      criteria.submodule && criteria.personId
        ? { submodule: criteria.submodule, personId: criteria.personId, device: criteria.device }
        : undefined
    );
  }
}
